import { Button } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import type { PassType } from '@/types';
import { TEST_IDS } from '@/testIds';
import { useDraftForm, clearDraft } from '@/shared/hooks/useDraftForm';
import {
  AppModal,
  CreditsStepper,
  DialogBox,
  DurationStepper,
  FieldError,
  ModalContent,
  ModalDeleteButton,
  ModalFooter,
  ModalTopBar,
  SectionTextField,
} from '@/shared/ui';
import {
  defaultTemplateFormValues,
  templateFormToDraft,
  templateToFormValues,
  type PassTemplateFormValues,
} from '../model';
import styles from './PassTemplateModal.module.css';

interface PassTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Omit<PassType, 'id'>) => void | Promise<void>;
  onDelete?: (id: string) => void | Promise<void>;
  editTemplate?: PassType;
  isSaving?: boolean;
}

const DRAFT_KEY = 'pass-template-create';

const validateTemplateCredits = (value: string, isUnlimited: boolean): true | string => {
  if (isUnlimited) return true;
  if (!value.trim()) return 'Credits are required unless unlimited';
  const parsed = parseInt(value.trim(), 10);
  return (!Number.isNaN(parsed) && parsed > 0) || 'Credits must be greater than zero';
};

const TemplateNameField: FC = () => {
  const nameErrorId = useId();
  const {
    control,
    formState: { errors },
  } = useFormContext<PassTemplateFormValues>();

  const { field } = useController({
    name: 'name',
    control,
    rules: {
      required: 'Name is required',
      validate: (value) => (value.trim().length > 0 ? true : 'Name is required'),
    },
  });

  return (
    <>
      <SectionTextField
        ref={field.ref}
        ariaDescribedBy={errors.name ? nameErrorId : undefined}
        label="Name"
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        name={field.name}
        ariaLabel="Pass template name"
        invalid={Boolean(errors.name)}
        testId={TEST_IDS.passTypeNameInput}
      />
      <FieldError id={nameErrorId} message={errors.name?.message?.toString()} />
    </>
  );
};

export const PassTemplateModal: FC<PassTemplateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  editTemplate,
  isSaving = false,
}) => {
  const formMethods = useForm<PassTemplateFormValues>({
    defaultValues: defaultTemplateFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = formMethods;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const operationRef = useRef(false);
  const isBusy = isSaving || isSubmitting || isDeleting;

  useEffect(() => {
    if (isOpen) reset(templateToFormValues(editTemplate));
  }, [editTemplate, isOpen, reset]);

  useDraftForm<PassTemplateFormValues>({
    key: DRAFT_KEY,
    watch,
    reset,
    enabled: !editTemplate && isOpen,
  });

  const handleSave = async (values: PassTemplateFormValues): Promise<void> => {
    if (operationRef.current || isSaving) return;
    operationRef.current = true;
    clearErrors('root');
    try {
      await onSave(templateFormToDraft(values));
      clearDraft(DRAFT_KEY);
    } catch {
      setError('root', {
        message: 'Could not save the template. Your changes are kept. Try again.',
      });
    } finally {
      operationRef.current = false;
    }
  };

  const handleClose = (): void => {
    if (isBusy || operationRef.current) return;
    setIsDeleteDialogOpen(false);
    onClose();
    if (!editTemplate) {
      clearDraft(DRAFT_KEY);
    }
  };

  const submitForm = handleSubmit(handleSave);

  const handleDelete = (): void => {
    if (isBusy || operationRef.current) return;
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!editTemplate || !onDelete || isBusy || operationRef.current) return;
    operationRef.current = true;
    setIsDeleting(true);
    clearErrors('root');
    try {
      await onDelete(editTemplate.id);
      setIsDeleteDialogOpen(false);
      onClose();
    } catch {
      setError('root', { message: 'Could not delete the template. Try again.' });
    } finally {
      operationRef.current = false;
      setIsDeleting(false);
    }
  };

  const cancelDelete = (): void => {
    if (isBusy || operationRef.current) return;
    setIsDeleteDialogOpen(false);
  };

  return (
    <FormProvider {...formMethods}>
      <AppModal
        open={isOpen}
        dismissible={!isBusy}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <form
          noValidate
          aria-label={editTemplate ? 'Edit template' : 'Create template'}
          onSubmit={(event) => {
            event.preventDefault();
            if (!isBusy && !operationRef.current) void submitForm();
          }}
        >
          <ModalContent>
            <ModalTopBar
              title={editTemplate ? 'Edit template' : 'Create template'}
              onCancel={handleClose}
              cancelDisabled={isBusy}
            />

            <fieldset className={styles.body} disabled={isBusy}>
              <TemplateNameField />
              <DurationStepper />
              <CreditsStepper
                validate={(value: string) =>
                  validateTemplateCredits(value, formMethods.getValues('isUnlimitedCredits'))
                }
              />
              <FieldError message={errors.root?.message} />
            </fieldset>

            <ModalFooter>
              <Button
                variant="primary"
                type="submit"
                fullWidth
                disabled={isSubmitting || isSaving}
                loading={isBusy}
                data-testid={TEST_IDS.saveTemplateButton}
              >
                {editTemplate ? 'Save changes' : 'Create template'}
              </Button>
              {editTemplate && (
                <ModalDeleteButton
                  label="Delete template"
                  onClick={handleDelete}
                  disabled={isBusy}
                  testId={TEST_IDS.deleteTemplateButton}
                />
              )}
            </ModalFooter>
          </ModalContent>
        </form>

        {editTemplate && (
          <DialogBox
            isOpen={isDeleteDialogOpen}
            message={
              <>
                {`Delete template "${editTemplate.name}"? This action cannot be undone.`}
                <FieldError message={errors.root?.message} />
              </>
            }
            confirmText="Delete"
            isDestructive
            isLoading={isBusy}
            confirmTestId={TEST_IDS.deleteTemplateConfirmButton}
            onConfirm={() => {
              void confirmDelete();
            }}
            onCancel={cancelDelete}
          />
        )}
      </AppModal>
    </FormProvider>
  );
};
